import { BLOCK_SHIFT, blockKey, blockFromKey, cellIndex } from './coords';
import type { Rwg1 } from './rwg1';

// ============================================================
// Grille locale d'un niveau : blocs 32 x 32 de cellules uint16 (idx du pays, 0 = aucun)
// ============================================================
// Lecture en O(1) par chunk. `apply` intègre une réponse RWG1 (complète ou delta) et renvoie les
// blocs touchés, pour que la couche ne redessine que les tuiles concernées, ainsi que la variation
// du nombre de chunks par idx, pour tenir les compteurs des pays à jour sans recharger la liste.
// ============================================================

export interface GridApplyResult {
  full: boolean;
  /** Clés des blocs dont le contenu a pu changer (en FULL : anciens et nouveaux). */
  changed: number[];
  /** idx → variation du nombre de chunks. */
  delta: Map<number, number>;
}

/**
 * Somme des centres de chunks d'un pays (cx + 0.5, cz + 0.5), nombre de chunks et emprise exacte.
 * Centroïde = (sx / n, sz / n) ; plusieurs pays s'additionnent (centroïde pondéré d'une région).
 */
export interface CountryCentroid {
  sx: number;
  sz: number;
  n: number;
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

export class ChunkGrid {
  readonly level: string;
  version = 0;
  /** A-t-on reçu au moins une réponse (même vide) ? */
  loaded = false;
  readonly blocks = new Map<number, Uint16Array>();
  /** Incrémenté à chaque apply : clé du cache des centroïdes (la version serveur peut ne pas bouger). */
  private rev = 0;
  private centroidCache: { rev: number; data: Map<number, CountryCentroid> } | null = null;

  constructor(level: string) {
    this.level = level;
  }

  get(cx: number, cz: number): number {
    const b = this.blocks.get(blockKey(cx >> BLOCK_SHIFT, cz >> BLOCK_SHIFT));
    return b ? b[cellIndex(cx, cz)] : 0;
  }

  getBlock(bx: number, bz: number): Uint16Array | undefined {
    return this.blocks.get(blockKey(bx, bz));
  }

  apply(msg: Rwg1): GridApplyResult {
    const delta = new Map<number, number>();
    const bump = (idx: number, d: number) => { if (idx) delta.set(idx, (delta.get(idx) ?? 0) + d); };
    const changed: number[] = [];

    if (msg.full) {
      for (const [key, cells] of this.blocks) {
        changed.push(key);
        for (let i = 0; i < cells.length; i++) bump(cells[i], -1);
      }
      this.blocks.clear();
      for (const b of msg.blocks) {
        if (!b.cells) continue;
        const key = blockKey(b.bx, b.bz);
        this.blocks.set(key, b.cells);
        changed.push(key);
        for (let i = 0; i < b.cells.length; i++) bump(b.cells[i], 1);
      }
    } else {
      for (const b of msg.blocks) {
        const key = blockKey(b.bx, b.bz);
        const prev = this.blocks.get(key);
        const next = b.cells;
        for (let i = 0; i < 1024; i++) {
          const a = prev ? prev[i] : 0;
          const n = next ? next[i] : 0;
          if (a !== n) { bump(a, -1); bump(n, 1); }
        }
        if (next && next.some((v) => v !== 0)) this.blocks.set(key, next);
        else this.blocks.delete(key);
        changed.push(key);
      }
    }
    this.version = msg.version;
    this.loaded = true;
    this.rev++;
    return { full: msg.full, changed, delta };
  }

  /**
   * idx → centroïde et emprise de ses chunks sur ce niveau (libellés des régions). Une passe sur
   * les blocs (environ 700 000 cellules sur la carte NG) dans des tableaux typés, mémorisée jusqu'au
   * prochain apply.
   */
  centroids(): Map<number, CountryCentroid> {
    if (this.centroidCache?.rev === this.rev) return this.centroidCache.data;
    const sx = new Float64Array(65536);
    const sz = new Float64Array(65536);
    const n = new Uint32Array(65536);
    const minX = new Int32Array(65536).fill(0x7fffffff);
    const maxX = new Int32Array(65536).fill(-0x80000000);
    const minZ = new Int32Array(65536).fill(0x7fffffff);
    const maxZ = new Int32Array(65536).fill(-0x80000000);
    for (const [key, cells] of this.blocks) {
      const { bx, bz } = blockFromKey(key);
      const x0 = bx << BLOCK_SHIFT;
      const z0 = bz << BLOCK_SHIFT;
      for (let i = 0; i < cells.length; i++) {
        const v = cells[i];
        if (!v) continue;
        const cx = x0 + (i & 31);
        const cz = z0 + (i >> 5);
        sx[v] += cx + 0.5;
        sz[v] += cz + 0.5;
        n[v]++;
        if (cx < minX[v]) minX[v] = cx;
        if (cx > maxX[v]) maxX[v] = cx;
        if (cz < minZ[v]) minZ[v] = cz;
        if (cz > maxZ[v]) maxZ[v] = cz;
      }
    }
    const data = new Map<number, CountryCentroid>();
    for (let v = 1; v < 65536; v++) {
      if (n[v]) data.set(v, { sx: sx[v], sz: sz[v], n: n[v], minX: minX[v], maxX: maxX[v], minZ: minZ[v], maxZ: maxZ[v] });
    }
    this.centroidCache = { rev: this.rev, data };
    return data;
  }

  /** Emprise exacte (en chunks) des cellules non nulles, null si la grille est vide. */
  bbox(): { minX: number; maxX: number; minZ: number; maxZ: number } | null {
    let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
    for (const [key, cells] of this.blocks) {
      const { bx, bz } = blockFromKey(key);
      for (let i = 0; i < cells.length; i++) {
        if (!cells[i]) continue;
        const cx = (bx << BLOCK_SHIFT) + (i & 31);
        const cz = (bz << BLOCK_SHIFT) + (i >> 5);
        if (cx < minX) minX = cx;
        if (cx > maxX) maxX = cx;
        if (cz < minZ) minZ = cz;
        if (cz > maxZ) maxZ = cz;
      }
    }
    return minX === Infinity ? null : { minX, maxX, minZ, maxZ };
  }

  /** idx → nombre de chunks (pour réconcilier les compteurs après un chargement complet). */
  counts(): Map<number, number> {
    const out = new Map<number, number>();
    for (const cells of this.blocks.values()) {
      for (let i = 0; i < cells.length; i++) {
        const v = cells[i];
        if (v) out.set(v, (out.get(v) ?? 0) + 1);
      }
    }
    return out;
  }
}
